/**
 * Pillole KPI della barra titolo sticky (prototipo L79-L85, dati L546-L552).
 * Sono 6 e non 5: "Da leggere" e il contatore operativo piu usato e resta
 * (ruling 8). La mutua esclusione con il filtro Stato e quella di oggi
 * (SupportCenterV2.tsx:99-103).
 */
import { STATUS_ACTIVE, activeCount, unreadCount } from '../constants';
import { formatDuration } from '../../../utils/formatDuration';
import type { DispatchStatsData, StatsData } from '../../../services/supportApi';

/** Filtri rapidi della vista «Smistamento» (Task 31); '' = nessuno. */
export type QueueQuickFilter = '' | 'dispatch' | 'unassignedOpen' | 'pending';

/**
 * Pillola «BOZZE» (Task 46): ticket non chiusi con risposte in bozza da
 * approvare. Solo quando il backend la calcola (`stats.pendingReplies`, presente
 * per chi puo approvare). Filtra con `pendingOnly` su tutti gli stati.
 */
const pendingKpi = (stats: StatsData): S3Kpi[] =>
  typeof stats.pendingReplies === 'number'
    ? [{
        key: 'pending', label: 'BOZZE', value: String(stats.pendingReplies),
        filter: { status: '', unread: false, quick: 'pending' as QueueQuickFilter }, clickable: true,
        hint: 'Mostra i ticket con risposte in bozza da approvare',
      }]
    : [];

export interface S3Kpi {
  key: string;
  label: string;
  value: string;
  /**
   * `null` sulle pillole informative: non filtrano e non si evidenziano.
   * `quick` (Task 31) e il filtro rapido della coda admin ('dispatch' = da smistare).
   */
  filter: { status: string; unread: boolean; quick?: QueueQuickFilter; slaBreach?: boolean } | null;
  clickable: boolean;
  /** Numero da evidenziare in rosso (v4: «FUORI SLA» maggiore di zero). */
  hot?: boolean;
  /**
   * Testo esplicativo del contatore, mostrato come `title`. E lo stesso delle
   * card di oggi (SupportCenterV2.tsx:477/484/491): la parita chiede che non
   * si perda passando alle pillole.
   */
  hint?: string;
}

export const buildKpis = (stats: StatsData | null): S3Kpi[] => {
  if (!stats) return [];
  return [
    {
      key: 'total', label: 'TOTALI', value: String(stats.total ?? 0),
      filter: { status: '', unread: false }, clickable: true,
      hint: 'Mostra tutti gli stati (gli altri filtri restano)',
    },
    {
      key: 'active', label: 'ATTIVI', value: String(activeCount(stats)),
      filter: { status: STATUS_ACTIVE, unread: false }, clickable: true,
      hint: 'Mostra solo i ticket non risolti/chiusi',
    },
    {
      key: 'unread', label: 'DA LEGGERE', value: String(unreadCount(stats)),
      filter: { status: STATUS_ACTIVE, unread: true }, clickable: true,
      hint: 'Mostra solo i ticket con messaggi del richiedente non letti o note interne da leggere',
    },
    // «Da smistare» (Task 31): solo quando il backend lo calcola (Centro Ticket admin).
    ...(typeof stats.toDispatch === 'number'
      ? [{
          key: 'dispatch', label: 'DA SMISTARE', value: String(stats.toDispatch),
          filter: { status: STATUS_ACTIVE, unread: false, quick: 'dispatch' as QueueQuickFilter }, clickable: true,
          hint: 'Mostra i ticket attivi nel canale predefinito (contatti dal sito ed email inoltrate)',
        }]
      : []),
    ...pendingKpi(stats),
    { key: 'waiting', label: 'IN ATTESA', value: String(stats.waiting ?? 0), filter: null, clickable: false },
    { key: 'resolved', label: 'RISOLTI NEL MESE', value: String(stats.resolvedThisMonth ?? 0), filter: null, clickable: false },
    { key: 'sla', label: '1ª RISPOSTA (MEDIA)', value: formatDuration(stats.avgFirstResponseMs), filter: null, clickable: false },
  ];
};

export const kpiIsActive = (
  kpi: S3Kpi,
  current: { status: string; unread: boolean; quick?: QueueQuickFilter; slaBreach?: boolean }
): boolean => {
  if (!kpi.clickable || !kpi.filter) return false;
  return (
    kpi.filter.status === current.status &&
    kpi.filter.unread === current.unread &&
    (kpi.filter.quick || '') === (current.quick || '') &&
    Boolean(kpi.filter.slaBreach) === Boolean(current.slaBreach)
  );
};

/**
 * KPI della bacheca dello Smistamento (redesign v4, T39; design L865, L880):
 * DA SMISTARE · NON PRESI IN CARICO · IN LAVORAZIONE · FUORI SLA · 1ª RISPOSTA.
 * Solo «FUORI SLA» e cliccabile (filtro `slaBreach` della bacheca) ed e `hot`
 * quando e maggiore di zero. Gli altri tre sono gia le colonne della bacheca:
 * informativi, come nel prototipo.
 *
 * `stats` = `getDispatchStats({ board: true })` SENZA `directOnly`: con
 * `directOnly` il perimetro si restringe ai canali diretti e `toDispatch`
 * sarebbe quasi sempre 0 (review T33). Senza i contatori della bacheca
 * (backend vecchio) le pillole relative si omettono. `buildKpis` non cambia.
 */
export const buildDispatchKpis = (stats: DispatchStatsData | null): S3Kpi[] => {
  if (!stats) return [];
  const info = (key: string, label: string, v: number | undefined): S3Kpi[] =>
    typeof v === 'number' ? [{ key, label, value: String(v), filter: null, clickable: false }] : [];
  const breached = stats.slaBreached;
  return [
    ...info('dispatch', 'DA SMISTARE', stats.toDispatch),
    ...info('notTaken', 'NON PRESI IN CARICO', stats.notTaken),
    ...info('inProgress', 'IN LAVORAZIONE', stats.inProgress),
    // Bozze da approvare (Task 46): informativa in bacheca, il filtro sta in «Elenco».
    ...info('pending', 'BOZZE', stats.pendingReplies),
    ...(typeof breached === 'number'
      ? [{
          key: 'slaBreach', label: 'FUORI SLA', value: String(breached),
          filter: { status: STATUS_ACTIVE, unread: false, slaBreach: true }, clickable: true,
          hot: breached > 0,
          hint: 'Mostra solo i ticket attivi con la scadenza SLA superata',
        }]
      : []),
    { key: 'sla', label: '1ª RISPOSTA (MEDIA)', value: formatDuration(stats.avgFirstResponseMs), filter: null, clickable: false },
  ];
};
